import { firestoreConstructors } from '@/plugins/firebase'

/** @enum {string} */
export const FIELD_SURVEY_STATUS = {
  NEW: 'new',
  IN_PROGRESS: 'inProgress',
  COMPLETED: 'completed'
}

const COLLECTION = 'app/v1/fieldSurveys'

/**
 * Normalize Firestore DocumentReference[] or raw refs to document ids.
 * @param {Array} refs
 * @returns {string[]}
 */
export function fieldSurveyRefsToIds (refs) {
  if (!refs || !Array.isArray(refs) || refs.length === 0) return []
  return refs
    .map((ref) => {
      if (!ref) return null
      if (typeof ref === 'string') return ref.split('/').pop()
      if (typeof ref.id === 'string') return ref.id
      if (typeof ref.path === 'string') return ref.path.split('/').pop()
      return null
    })
    .filter(Boolean)
}

function createdAtToMillis (createdAt) {
  if (!createdAt) return 0
  if (typeof createdAt.toMillis === 'function') return createdAt.toMillis()
  if (typeof createdAt.toDate === 'function') return createdAt.toDate().getTime()
  if (createdAt instanceof Date) return createdAt.getTime()
  return 0
}

/**
 * Fetch field survey documents by id (batched).
 * @param {import('firebase').firestore.Firestore} db
 * @param {string[]} ids
 * @returns {Promise<Array<{ id: string, status: string, createdAt: * }>>}
 */
export async function fetchFieldSurveyDocuments (db, ids) {
  if (!ids || ids.length === 0) return []
  const unique = [...new Set(ids)]
  const snapshots = []
  while (unique.length) {
    const batch = unique.splice(0, 10)
    const q = db.collection(COLLECTION).where(firestoreConstructors.FieldPath.documentId(), 'in', batch)
    snapshots.push(q.get())
  }
  const results = await Promise.all(snapshots)
  const out = []
  results.forEach((snap) => {
    snap.forEach((doc) => {
      const d = doc.data() || {}
      out.push({
        id: doc.id,
        status: d.status,
        createdAt: d.createdAt
      })
    })
  })
  return out
}

function isPendingStatus (status) {
  return status === FIELD_SURVEY_STATUS.NEW || status === FIELD_SURVEY_STATUS.IN_PROGRESS
}

function isCompletedStatus (status) {
  return status === FIELD_SURVEY_STATUS.COMPLETED
}

/**
 * @param {Array<{ id: string, status: string, createdAt: * }>} documents
 * @returns {{ pending: Array<{ id: string, createdAtMs: number }>, hasCompleted: boolean, oldestPendingId: string|null }}
 */
export function classifyFieldSurveys (documents) {
  const pending = []
  let hasCompleted = false
  documents.forEach((doc) => {
    if (isCompletedStatus(doc.status)) {
      hasCompleted = true
    }
    if (isPendingStatus(doc.status)) {
      pending.push({
        id: doc.id,
        createdAtMs: createdAtToMillis(doc.createdAt)
      })
    }
  })
  pending.sort((a, b) => a.createdAtMs - b.createdAtMs)
  const oldestPendingId = pending.length ? pending[0].id : null
  return { pending, hasCompleted, oldestPendingId }
}

/**
 * After login: redirect to field survey when there is pending work and none completed yet.
 * @param {{ hasCompleted: boolean, oldestPendingId: string|null }} classification
 * @returns {string|null} fieldSurveyId to open
 */
export function getLoadingScreenFieldSurveyId (classification) {
  if (!classification.oldestPendingId) return null
  if (classification.hasCompleted) return null
  return classification.oldestPendingId
}

/**
 * On audits home: auto-redirect when pending exists and none completed.
 * @param {{ hasCompleted: boolean, oldestPendingId: string|null }} classification
 * @returns {string|null}
 */
export function getAuditsAutoRedirectFieldSurveyId (classification) {
  return getLoadingScreenFieldSurveyId(classification)
}

/**
 * Show popup on audits when there is at least one pending and at least one completed.
 * @param {{ pending: Array, hasCompleted: boolean }} classification
 * @returns {boolean}
 */
export function shouldShowAuditsPendingPopup (classification) {
  return classification.hasCompleted && classification.pending.length > 0
}

/**
 * @param {import('firebase').firestore.Firestore} db
 * @param {string[]} fieldSurveyIds
 */
export async function resolveFieldSurveyClassification (db, fieldSurveyIds) {
  if (!fieldSurveyIds || fieldSurveyIds.length === 0) {
    return {
      pending: [],
      hasCompleted: false,
      oldestPendingId: null
    }
  }
  const documents = await fetchFieldSurveyDocuments(db, fieldSurveyIds)
  return classifyFieldSurveys(documents)
}

/**
 * Sets field survey status to in-progress when the user opens the setup route (`/fieldSurveys/:id`).
 * Only transitions from `new`; never overwrites `completed` or `inProgress`.
 * @param {import('firebase').firestore.Firestore} db
 * @param {string} fieldSurveyId
 */
export async function markFieldSurveyInProgressForSetup (db, fieldSurveyId) {
  if (!fieldSurveyId) return
  const ref = db.collection(COLLECTION).doc(fieldSurveyId)
  const snap = await ref.get()
  if (!snap.exists) return
  if (String(((snap.data() || {}).status) || FIELD_SURVEY_STATUS.NEW) !== FIELD_SURVEY_STATUS.NEW) return
  await ref.update({ status: FIELD_SURVEY_STATUS.IN_PROGRESS })
}



// WEBPACK FOOTER //
// ./src/lib/fieldSurveysRouting.js